import React, { useEffect, useState } from 'react';
import { FiRefreshCw, FiAlertTriangle, FiPlay, FiServer, FiStopCircle } from 'react-icons/fi';
import { authFetch } from '../auth';

const fmtBytes = (n) => {
  if (!n && n !== 0) return '';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let x = Number(n), u = 0;
  while (x >= 1024 && u < units.length - 1) { x /= 1024; u++; }
  return `${x.toFixed(1)} ${units[u]}`;
};

export default function AdminStatusView({ API_URL }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [status, setStatus] = useState(null);
  const [ocrHealth, setOcrHealth] = useState(null);
  const [backfillLimit, setBackfillLimit] = useState(200);
  const [repairLimit, setRepairLimit] = useState(200);
  const [editing, setEditing] = useState(false);
  const [settings, setSettings] = useState(null);
  const [cacheRefreshing, setCacheRefreshing] = useState(false);

  const load = async () => {
    setLoading(true); setErr('');
    try {
      const res = await authFetch(`${API_URL}/admin/status`);
      if (!res.ok) throw new Error((await res.json().catch(()=>({}))).message || 'Failed to load status');
      const j = await res.json();
      setStatus(j);
      try {
        const sRes = await authFetch(`${API_URL}/admin/settings`);
        if (sRes.ok) {
          const sj = await sRes.json();
          // Don't clobber in-progress edits; only hydrate when not editing
          if (!editing) setSettings(sj.settings || {});
        }
      } catch (_) {}
      try {
        const h = await fetch(`${API_URL}/ocr/health`).then(r => r.json()).catch(() => null);
        setOcrHealth(h);
      } catch (_) {}
    } catch (e) {
      setErr(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const rebuildFileCache = async () => {
    setCacheRefreshing(true);
    setErr('');
    try {
      const res = await authFetch(`${API_URL}/files?refresh=1`);
      if (!res.ok) throw new Error((await res.json().catch(()=>({}))).message || 'Failed to rebuild file cache');
      await res.json().catch(() => []);
      try { window.spartaToast && window.spartaToast('File cache refreshed.', 'success', 2500); } catch (_) {}
    } catch (e) {
      setErr(e?.message || 'Failed to rebuild file cache');
    } finally {
      setCacheRefreshing(false);
    }
  };

  // While editing settings, pause background toasts that can cause re-renders and close dropdowns
  useEffect(() => {
    try {
      window.spartaPauseToasts = editing;
      window.spartaPauseUIUpdates = editing; // also pause UI state updates from pollers
    } catch (_) {}
    return () => {
      try {
        if (editing) {
          window.spartaPauseToasts = false;
          window.spartaPauseUIUpdates = false;
        }
      } catch (_) {}
    };
  }, [editing]);

  // Removed auto-refresh to respect manual refresh only

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}><FiServer /> Server Status</h2>
        <button className="btn" onClick={load} disabled={loading} title="Refresh">
          <FiRefreshCw style={{ verticalAlign: 'middle' }} /> Refresh
        </button>
      </div>
      {err && (
        <div style={{ marginTop: 10, padding: 10, border: '1px solid #e57373', background: '#ffebee', color: '#b71c1c', borderRadius: 6 }}>
          <FiAlertTriangle /> {err}
        </div>
      )}
      {!err && !status && (
        <div style={{ marginTop: 12 }}>Loading…</div>
      )}
      {status && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
          <div style={card}>
            <h3 style={h3}>Process</h3>
            <Row k="PID" v={status.process?.pid} />
            <Row k="Node" v={status.process?.node} />
            <Row k="Platform" v={`${status.process?.platform} ${status.process?.arch}`} />
            <Row k="Uptime" v={`${Math.round((status.process?.uptime_sec||0)/60)} min`} />
            <Row k="Load Avg" v={(status.process?.loadavg||[]).map(n=>n.toFixed(2)).join(' / ')} />
            <Row k="RSS" v={fmtBytes(status.process?.memory?.rss)} />
            <Row k="Heap Used" v={fmtBytes(status.process?.memory?.heapUsed)} />
          </div>
          <div style={card}>
            <h3 style={h3}>Storage</h3>
            <Row k="Upload Dir" v={status.storage?.uploadDir} mono />
            <Row k="Text Dir" v={status.storage?.textDir} mono />
            <Row k="PDFs" v={status.storage?.pdfCount} />
            <Row k="Sidecars" v={`${status.storage?.sidecarNonEmptyCount}/${status.storage?.sidecarCount}`} />
            <button
              className="btn"
              style={{ marginTop: 10 }}
              onClick={rebuildFileCache}
              disabled={cacheRefreshing}
              title="Force a rescan of the uploads directory and rebuild the file metadata cache"
            >
              <FiRefreshCw style={{ verticalAlign: 'middle' }} /> {cacheRefreshing ? 'Rebuilding…' : 'Rebuild File Cache'}
            </button>
          </div>
          <div style={card}>
            <h3 style={h3}>OCR</h3>
            <Row k="Active" v={String(status.ocr?.active)} />
            <Row k="Paused" v={String(Boolean(status.ocr && status.ocr.paused))} />
            <Row k="Current" v={status.ocr?.current || '—'} mono />
            <Row k="Processed" v={`${status.ocr?.processed} / ${status.ocr?.total}`} />
            <Row k="Queue Length" v={status.ocr?.queueLength} />
            <Row k="Message" v={status.ocr?.message || '—'} />
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
              <label style={{ marginRight: 8 }}>Backfill missing text:</label>
              <input type="number" min={1} value={backfillLimit} onChange={(e)=>setBackfillLimit(parseInt(e.target.value||'1',10))} style={input} />
              <button
                className="btn btn-primary"
                style={{ marginLeft: 8 }}
                onClick={async () => {
                  try {
                    setErr('');
                    await authFetch(`${API_URL}/ocr/backfill?limit=${encodeURIComponent(backfillLimit)}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
                    await load();
                  } catch (e) { setErr(e?.message || 'Backfill failed'); }
                }}
                title="Enqueue PDFs missing sidecar text"
              >
                <FiPlay style={{ verticalAlign: 'middle' }} /> Backfill
              </button>
              {status.ocr?.paused ? (
                <button
                  className="btn"
                  style={{ marginLeft: 8 }}
                  onClick={async () => { try { await authFetch(`${API_URL}/ocr/resume`, { method: 'POST' }); await load(); } catch (_) {} }}
                  title="Resume OCR processing"
                >
                  Resume OCR
                </button>
              ) : (
                <button
                  className="btn btn-clear"
                  style={{ marginLeft: 8 }}
                  onClick={async () => { try { await authFetch(`${API_URL}/ocr/pause`, { method: 'POST' }); await load(); } catch (_) {} }}
                  title="Pause OCR after current file"
                >
                  Pause OCR
                </button>
              )}
              <button
                className="btn"
                style={{ marginLeft: 8, backgroundColor: '#c62828', color: '#fff', borderColor: '#c62828' }}
                disabled={!(status.ocr?.active || status.ocr?.current || (status.ocr?.queueLength || 0) > 0)}
                onClick={async () => {
                  try {
                    if (!window.confirm('Stop OCR and clear the entire queue?')) return;
                    setErr('');
                    const res = await authFetch(`${API_URL}/ocr/stop`, { method: 'POST' });
                    if (!res.ok) throw new Error((await res.json().catch(()=>({}))).message || 'Failed to stop OCR');
                    try { window.spartaToast && window.spartaToast('OCR stopped and queue cleared.', 'success', 3500); } catch (_) {}
                    await load();
                  } catch (e) { setErr(e?.message || 'Stop OCR failed'); }
                }}
                title="Immediately kill OCR processing and empty the queue"
              >
                <FiStopCircle style={{ verticalAlign: 'middle' }} /> Stop OCR
              </button>
            </div>
          </div>
          <div style={card}>
            <h3 style={h3}>File Repair</h3>
            <Row k="Active" v={String(status.repair?.active)} />
            <Row k="Progress" v={`${status.repair?.processed || 0} / ${status.repair?.total || 0}`} />
            <Row k="Message" v={status.repair?.message || '—'} />
            <Row k="Last Run" v={status.repair?.lastRunAt ? new Date(status.repair.lastRunAt).toLocaleString() : '—'} />
            <div style={{ marginTop: 8 }}>
              <label style={{ marginRight: 8 }}>Repair limit:</label>
              <input type="number" min={1} value={repairLimit} onChange={(e)=>setRepairLimit(parseInt(e.target.value||'1',10))} style={input} />
              <button
                className="btn btn-primary"
                style={{ marginLeft: 8 }}
                onClick={async () => {
                  try {
                    setErr('');
                    await authFetch(`${API_URL}/admin/repair/start`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ limit: repairLimit }) });
                    await load();
                  } catch (e) { setErr(e?.message || 'Start repair failed'); }
                }}
                title="Start background file repair"
                disabled={status.repair?.active}
              >
                Start
              </button>
              <button
                className="btn btn-clear"
                style={{ marginLeft: 8 }}
                onClick={async () => {
                  try {
                    setErr('');
                    await authFetch(`${API_URL}/admin/repair/stop`, { method: 'POST' });
                    await load();
                  } catch (e) { setErr(e?.message || 'Stop repair failed'); }
                }}
                title="Stop background file repair"
                disabled={!status.repair?.active}
              >
                Stop
              </button>
          </div>
          </div>
          <div style={card}>
            <h3 style={h3}>Settings</h3>
            {(() => {
              const entries = Object.entries(status.settings || {});
              const preferred = ['OCR_ENABLED','OCR_TMP_DIR'];
              const preferredRows = preferred
                .filter(k => entries.some(([ek]) => ek === k))
                .map(k => [k, entries.find(([ek]) => ek === k)[1]]);
              const remaining = entries.filter(([k]) => !preferred.includes(k));
              return [...preferredRows, ...remaining].map(([k, v]) => (
                <Row key={k} k={k} v={String(v)} />
              ));
            })()}
            <div style={{ marginTop: 8 }}>
              <button
                className="btn"
                onClick={() => {
                  // Initialize editor with latest status settings when entering edit mode
                  if (!editing) {
                    const latest = (status && status.settings) ? { ...status.settings } : (settings || {});
                    setSettings(latest);
                  }
                  setEditing(!editing);
                }}
              >
                {editing ? 'Close Editor' : 'Edit Settings'}
              </button>
            </div>
            {editing && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--border)' }}>
                <h4 style={{ margin: '0 0 6px' }}>Edit (restart required)</h4>
                <SettingsEditor
                  settings={settings || {}}
                  onChange={(k, val) => setSettings(s => ({ ...(s||{}), [k]: val }))}
                  onSave={async () => {
                    try {
                      setErr('');
                      const res = await authFetch(`${API_URL}/admin/settings`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ settings }) });
                      if (!res.ok) throw new Error((await res.json().catch(()=>({}))).message || 'Failed to save settings');
                      try { window.spartaToast && window.spartaToast('Settings saved. Restart server to apply.', 'success', 3500); } catch (_) {}
                    } catch (e) { setErr(e?.message || 'Failed to save'); }
                  }}
                />
              </div>
            )}
            <h4 style={{ marginTop: 10, marginBottom: 6 }}>OCR Health</h4>
            <div style={{ fontSize: 13, color: 'var(--text)' }}>
              {ocrHealth?.ok ? `OK: ${ocrHealth.version || ''}` : (ocrHealth?.message || 'Unknown')}
            </div>
          </div>
          <div style={card}>
            <h3 style={h3}>Downloads</h3>
            <div style={{ fontSize: 14, color: 'var(--text)' }}>
              <div style={{ marginBottom: 6 }}>
                Windows scan station installer for Dynamic Web TWAIN:
              </div>
              <a
                href="/DynamicWebTWAIN19.2.exe"
                title="Download Dynamic Web TWAIN 19.2 for Windows"
                className="btn btn-primary"
              >
                Download DynamicWebTWAIN19.2.exe
              </a>
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-soft)' }}>
                Place this file in the site's public root (client/public). The link serves it from the web root.
              </div>
            </div>
          </div>
          <div style={{ ...card, gridColumn: '1 / span 2' }}>
            <h3 style={h3}>Queue Sample</h3>
            <div style={{ maxHeight: 200, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 6 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={th}>#</th>
                    <th style={th}>Filename</th>
                  </tr>
                </thead>
                <tbody>
                  {(status.ocr?.sampleQueue||[]).map((f,i) => (
                    <tr key={i} style={{ background: i%2 ? 'var(--row-odd-bg)' : 'var(--row-even-bg)' }}>
                      <td style={td}>{i+1}</td>
                      <td style={{ ...td, fontFamily: 'monospace' }}>{f}</td>
                    </tr>
                  ))}
                  {(!status.ocr?.sampleQueue || status.ocr.sampleQueue.length === 0) && (
                    <tr><td colSpan={2} style={{ ...td, textAlign: 'center' }}>No queued items</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ k, v, mono }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
      <div style={{ color: 'var(--text-soft)' }}>{k}</div>
      <div style={{ fontFamily: mono ? 'monospace' : 'inherit', color: 'var(--text)' }}>{v}</div>
    </div>
  );
}

const card = { padding: 12, background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8 };
const h3 = { margin: 0, marginBottom: 8, fontSize: 16 };
const th = { textAlign: 'left', padding: '6px 8px', background: 'var(--table-head-bg)', borderBottom: '1px solid var(--border)' };
const td = { padding: '6px 8px', borderBottom: '1px solid var(--border)' };
const input = { padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 6, width: 100 };

function SettingsEditor({ settings, onChange, onSave }) {
  const Item = ({ k, type='text', width=200 }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
      <div style={{ width: 220, color: 'var(--text-soft)' }}>{k}</div>
      {type === 'bool' ? (
        <select value={String(settings[k] ?? '')} onChange={(e)=>onChange(k, e.target.value)} style={{ ...input, width }}>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      ) : (
        <input value={String(settings[k] ?? '')} onChange={(e)=>onChange(k, e.target.value)} style={{ ...input, width }} />
      )}
    </div>
  );
  return (
    <div>
      <Item k="OCR_ENABLED" type="bool" />
      <Item k="OCRMYPDF_BIN" />
      <Item k="OCR_LANG" />
      <Item k="OCR_JOBS" width={80} />
      <Item k="OCR_OMP_THREADS" width={80} />
      <Item k="OCR_TIMEOUT_MS" width={120} />
      <Item k="OCR_TESSERACT_TIMEOUT_SEC" width={100} />
      <Item k="OCR_OPTIMIZE" width={80} />
      <Item k="OCR_QUIET" type="bool" />
      <Item k="OCR_TMP_DIR" />
      <Item k="FILES_REPAIR_ON_LIST" type="bool" />
      <Item k="FILES_REPAIR_LIMIT" width={100} />
      <Item k="OCR_BACKFILL_LIMIT" width={100} />
      <Item k="TEXT_MIN_NONWS" width={100} />
      <div style={{ marginTop: 8 }}>
        <button className="btn btn-primary" onClick={onSave}>Save Settings</button>
        <span style={{ marginLeft: 8, color: 'var(--text-soft)' }}>(Restart server to apply)</span>
      </div>
    </div>
  );
}
